import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { Button } from '../../../common/Button';
import { Dropdown } from '../../../common/Dropdown';
import { Icon } from '../../../common/Icon';
import { Input } from '../../../common/Input';
import { RadioGroup } from '../../../common/RadioGroup';
import { Slider } from '../../../common/Slider';
import { TextArea } from '../../../common/TextArea';
import { initialCheckInData, MAX_VALIDATION_ATTEMPTS } from '../constants';
import { getTicketPreviousStatus } from '../queries/getTicketPreviousStatus';
import {
  CheckInFormProps,
  CheckInTypeEnum,
  TicketOption,
} from '../types/types';

import { AssessmentWarning } from './AssessmentWarning';
import { CheckInExample } from './CheckInExample';
import { CheckInSuggestion } from './CheckInSuggestion';

const getButtonText = (
  isSubmitting: boolean,
  isValidating: boolean,
): string => {
  if (isValidating) return 'Validating...';
  if (isSubmitting) return 'Submitting...';
  return 'Submit';
};

export function CheckInForm({
  formState,
  setFormState,
  tickets,
  onSubmit,
  isSubmitting,
  isFormValid,
  assessment,
  setAssessment,
  isValidating,
  setValidationCount,
}: CheckInFormProps): JSX.Element {
  // We're keeping isDescriptionValid for potential future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const { data: ticketPreviousStatus } = useQuery({
    queryKey: ['ticketPreviousStatus', formState.ticketId],
    queryFn: () => getTicketPreviousStatus(formState.ticketId),
    enabled: !!formState.ticketId && !formState.isUpdate,
  });
  useEffect(() => {
    if (!formState.isUpdate) {
      setFormState((prev) => ({
        ...prev,
        prevProgress: ticketPreviousStatus?.[0]?.completion_percentage || 0,
        blockers: ticketPreviousStatus?.[0]?.blockers || '',
        wipMaterials: ticketPreviousStatus?.[0]?.wip_links || '',
        hasBlockers: !!(
          ticketPreviousStatus?.[0]?.blockers &&
          ticketPreviousStatus?.[0]?.blockers !== ''
        ),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketPreviousStatus, formState.isUpdate]);
  // Reset validation state when description changes
  const handleDescriptionChange = (text: string): void => {
    setFormState((prev) => ({ ...prev, description: text }));
    if (
      assessment.currentAssessment &&
      !assessment.currentAssessment.isAiSuggestionAccepted
    ) {
      setAssessment({
        ...assessment,
        currentAssessment: {
          ...assessment.currentAssessment,
          isAiSuggestionAccepted: true,
        },
      });
    }
  };
  return (
    <div className="flex flex-col justify-start items-center gap-6 w-[64%]  self-stretch mr-8">
      <div className="w-full flex flex-col items-start p-4 gap-6 self-stretch rounded-lg border  border-gray-300 bg-[#FDFAF8]">
        <RadioGroup
          label="Select Task Type"
          options={[
            {
              label: 'Ticket',
              value: CheckInTypeEnum.TICKET,
              icon: <Icon name="checkbox" />,
              iconPosition: 'end',
            },
            {
              label: 'Ad-Hoc',
              value: CheckInTypeEnum.AD_HOC,
              icon: <Icon name="timer" />,
              iconPosition: 'end',
            },
          ]}
          value={formState.type}
          onChange={(value: string) => {
            setFormState({
              ...initialCheckInData,
              type: value as CheckInTypeEnum,
            });
            setAssessment({
              results: [],
              currentAssessment: null,
            });
            setValidationCount(0);
          }}
        />
        {formState.type === CheckInTypeEnum.TICKET ? (
          <Dropdown<TicketOption>
            name="ticketId"
            label="Select Jira Ticket"
            options={tickets}
            variant="full"
            value={tickets.find((t) => t.value === formState.ticketId) ?? null}
            onChange={(option: TicketOption | null) => {
              if (formState.ticketId === option?.value) return;
              setFormState((prev) => ({
                ...initialCheckInData,
                type: prev.type,
                ticketId: option?.value,
                prevProgress: option?.completionPercentage ?? 0,
                progress: option?.completionPercentage ?? 0,
              }));
              setValidationCount(0);
            }}
            required
            placeholder="Select a ticket"
          />
        ) : (
          <Input
            label="Title"
            name="adhoc_title"
            id="adhoc_title"
            required
            placeholder="Adhoc Title"
            value={formState.title}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />
        )}
        <TextArea
          label="Describe briefly what you did on this task"
          guideLines={
            <CheckInExample assessment={assessment.currentAssessment} />
          }
          id="description"
          required
          primaryPlaceholder="For example: Completed payment gateway integration. Verified transactions with credit card and PayPal in staging."
          showCheckbox={formState.type === CheckInTypeEnum.TICKET}
          blockersText={formState.blockers}
          primaryText={formState.description}
          isChecked={!formState.hasBlockers}
          onPrimaryTextChange={handleDescriptionChange}
          onBlockersTextChange={(text: string) => {
            setFormState((prev) => ({ ...prev, blockers: text }));
          }}
          onCheckedChange={(checked: boolean) => {
            setFormState((prev) => ({
              ...prev,
              hasBlockers: !checked,
              blockers: '',
            }));
          }}
          disabled={
            formState.type === CheckInTypeEnum.TICKET && !formState.ticketId
          }
          checkboxLabel="No blockers"
          secondaryLabel="Mention your blockers here"
          primarySuggestion={
            assessment?.currentAssessment &&
            !assessment.currentAssessment.passed_assessment ? (
              <>
                {assessment.currentAssessment.assessment_reasoning && (
                  <AssessmentWarning
                    warning={assessment.currentAssessment.assessment_reasoning}
                  />
                )}
                {assessment?.currentAssessment
                  ?.suggested_rewritten_check_in && (
                  <CheckInSuggestion
                    suggestion={
                      assessment.currentAssessment.suggested_rewritten_check_in
                    }
                    setSuggestion={() => {
                      setFormState({
                        ...formState,
                        description:
                          assessment?.currentAssessment
                            ?.suggested_rewritten_check_in ?? '',
                      });
                      setValidationCount(MAX_VALIDATION_ATTEMPTS);
                      if (assessment.currentAssessment) {
                        setAssessment({
                          ...assessment,
                          currentAssessment: {
                            ...assessment.currentAssessment,
                            isAiSuggestionAccepted: true,
                          },
                        });
                      }
                    }}
                  />
                )}
              </>
            ) : null
          }
        />
        {formState.type === CheckInTypeEnum.TICKET && (
          <>
            <Input
              label="Add WIP materials related to this task"
              id="wipMaterials"
              name="wipMaterials"
              disabled={!formState.ticketId}
              placeholder="Paste links of file or project etc."
              value={formState.wipMaterials}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  wipMaterials: e.target.value,
                }))
              }
            />
            <Slider
              disabled={!formState.ticketId}
              label="Task Progress Achieved( in % )"
              initialValue={formState.prevProgress}
              value={formState.progress}
              step={1}
              onChange={(value) =>
                setFormState((prev) => ({
                  ...prev,
                  progress: value,
                }))
              }
            />
          </>
        )}
      </div>
      <div className="flex flex-row w-full items-end justify-end">
        <Button
          variant="primary"
          size="md"
          className="bg-[#CE3F4F]"
          onClick={() => {
            onSubmit().catch(console.error);
          }}
          disabled={isSubmitting || !isFormValid || isValidating}
        >
          {getButtonText(isSubmitting, isValidating)}
        </Button>
      </div>
    </div>
  );
}
